import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import {
  getImportBatchSummary,
  listMembers,
  onMembersChanged,
  undoImportBatch,
  validateApiResponse,
} from '../../../services';
import { MEMBER_STATUS } from '../constants/copy';
import { mapApiMemberToUi, UI_STATUS_TO_API } from '../utils/mapApiMemberToUi';

const NOT_INVITED_PAGE_SIZE = 500;

/**
 * The import batch the Members list is filtered to (banner, "Select the N not invited" and
 * "Undo import"). Reads its summary again whenever a member changes anywhere in the app.
 *
 * @param {string} batchId The applied batch ('' for none).
 * @returns {{
 *   batch: { id: string, file: string, date: string, memberCount: number, notInvited: number } | null,
 *   fetchNotInvited: () => Promise<object[]>,
 *   undo: () => Promise<boolean>,
 *   isUndoing: boolean,
 * }}
 */
export const useImportBatchBanner = (batchId) => {
  const { t } = useTranslation('people-access');
  const toast = useToast();
  const [batch, setBatch] = useState(null);
  const [isUndoing, setIsUndoing] = useState(false);
  const mountedRef = useRef(true);
  const undoingRef = useRef(false);
  const fetchIdRef = useRef(0);

  const refetch = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    if (!batchId) {
      setBatch(null);
      return;
    }
    // Another batch than the one on screen: do not show the old one while this loads.
    setBatch((prev) => (prev && prev.id === batchId ? prev : null));
    try {
      const res = await getImportBatchSummary(batchId);
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      if (res.status_code === 404) {
        // A batch the service does not know (a stale ?batch= link) shows no banner.
        setBatch(null);
        return;
      }
      const { body } = validateApiResponse(res, '');
      setBatch({
        id: body.id,
        file: body.file_name ?? '',
        date: body.created_at,
        memberCount: body.member_count,
        notInvited: body.not_invited_count,
      });
    } catch {
      // Any other failure keeps what the banner already shows; the next change refreshes it.
    }
  }, [batchId]);

  useEffect(() => {
    mountedRef.current = true;
    refetch();
    const off = onMembersChanged(refetch);
    return () => {
      mountedRef.current = false;
      off();
    };
  }, [refetch]);

  // Every not-invited person of the batch, page after page (a batch may hold more than one page).
  const fetchNotInvited = useCallback(async () => {
    const people = [];
    for (let page = 1; ; page += 1) {
      const res = await listMembers({
        page,
        page_size: NOT_INVITED_PAGE_SIZE,
        filters: { import_batch_id: batchId, status: UI_STATUS_TO_API[MEMBER_STATUS.NOT_INVITED] },
      });
      const items = validateApiResponse(res, '').body.items.map(mapApiMemberToUi);
      people.push(...items);
      if (items.length < NOT_INVITED_PAGE_SIZE) return people;
    }
  }, [batchId]);

  const undo = useCallback(async () => {
    if (undoingRef.current) return false;
    undoingRef.current = true;
    setIsUndoing(true);
    try {
      const { body } = validateApiResponse(await undoImportBatch(batchId), '');
      toast.success({
        title: t('members.importBatch.undone.title'),
        message: t('members.importBatch.undone.message', { count: body.deleted_count }),
      });
      return true;
    } catch (err) {
      toast.error({
        title: t('members.importBatch.undone.errorTitle'),
        message: err?.message || t('members.importBatch.undone.errorMessage'),
      });
      return false;
    } finally {
      undoingRef.current = false;
      if (mountedRef.current) setIsUndoing(false);
    }
  }, [batchId, toast, t]);

  return { batch, fetchNotInvited, undo, isUndoing };
};
